import React from "react";
import { Drawer, Flex, Text } from "@mantine/core";

import useStyles from "./style";
import { Card } from "./markMedication";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import moment from "moment";
import { getActiveSlotForDate } from "../../utils";
import missedMed from "../../images/missedMed.png";
import upcommingMed from "../../images/upcommingMed.png";
import takenMed from "../../images/takenMed.png";
import refusedMed from "../../images/refusedMed.png";
import outOfMed from "../../images/outOfMed.png";
import unableToTakeMed from "../../images/unableToTakeMed.png";

const getStatusImage = (status) => {
  if (status === "Taken") return takenMed;
  if (status === "Refused") return refusedMed;
  if (status === "Unable to Take") return unableToTakeMed;
  if (status === "Out of Medication") return outOfMed;
  if (status === "Missed") return missedMed;
  return upcommingMed;
};

// Resolve the correct status string from a MAR entry.
// data.medicationType is an array in the new schema — find the entry matching
// data.date + data.timeSlot, falling back to date-only, then first entry.
const resolveStatus = (data) => {
  const medType = data?.medicationType;
  if (!medType) return "upcoming";
  if (typeof medType === "string") return medType;
  if (!Array.isArray(medType)) return "upcoming";
  if (medType.length === 0)
    return moment(data?.date).isBefore(moment().startOf("day"))
      ? "Missed"
      : "upcoming";

  const targetDateStr = moment(data?.date).format("YYYY-MM-DD");
  const slot = getActiveSlotForDate(data?.timeSlot, data?.date);

  // 1. Exact: date + timeSlot
  const exact = medType.filter(
    (m) =>
      moment(m.date).format("YYYY-MM-DD") === targetDateStr &&
      m.timeSlot === slot,
  );
  if (exact.length > 0) return exact[exact.length - 1].status;

  // 2. Date + no timeSlot on entry (virtual entries only store status + date)
  const dateOnly = medType.filter(
    (m) => moment(m.date).format("YYYY-MM-DD") === targetDateStr && !m.timeSlot,
  );
  if (dateOnly.length > 0) return dateOnly[dateOnly.length - 1].status;

  // 3. Single-entry virtual entries
  if (medType.length === 1) return medType[0].status;

  return moment(data?.date).isBefore(moment().startOf("day"))
    ? "Missed"
    : "upcoming";
};

const MarDetailModal = ({ data, date, calendar, triggerLabel }) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <div
        className={classes.moreRow}
        style={
          triggerLabel
            ? { margin: 0, padding: 0, lineHeight: 1, justifyContent: "center" }
            : undefined
        }
      >
        <p
          onClick={() => open()}
          style={
            triggerLabel
              ? {
                  margin: 0,
                  padding: "2px 0",
                  lineHeight: 1,
                  textAlign: "center",
                  width: "100%",
                }
              : undefined
          }
        >
          {triggerLabel ?? `${data?.length - 5} more...`}
        </p>
      </div>
      <Drawer
        opened={opened}
        onClose={close}
        closeOnClickOutside={false}
        offset={isMobile ? 0 : 8}
        size={isMobile ? "100%" : 690}
        sx={{
          ".mantine-Drawer-close": {
            color: "#000",
            "& svg": {
              width: "25px !important",
              height: "25px !important",
            },
          },
          ".mantine-Drawer-content": {
            background: "#F5F5F5",
            borderTopLeftRadius: isMobile ? 0 : 16,
            borderBottomLeftRadius: isMobile ? 0 : 16,
          },
          ".mantine-Drawer-close:hover": {
            color: "#FFAF4A",
          },
          ".mantine-1q36a81": {
            padding: "0",
          },
          ".mantine-Drawer-body": {
            padding: isMobile ? "14px" : "22px",
          },
        }}
        zIndex={1000}
        position="right"
        overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
        shadow="md"
        title={
          <Flex direction="column">
            <Text fz={24} fw={700} ff="Inter" color="#000">
              MAR
            </Text>
            <Text fz={13} fw={400} ff="Inter" color="#5C5C5C">
              {date}
            </Text>
          </Flex>
        }
      >
        <div>
          {data
            ?.sort(function (a, b) {
              return new Date(a?.date) - new Date(b?.date);
            })
            ?.map((v, i) => {
              return (
                <div key={i} className={classes.cardWrapper}>
                  <Card
                    data={v}
                    allData={data}
                    calendar={calendar}
                    date={date}
                  />
                </div>
              );
            })}
        </div>
      </Drawer>
    </>
  );
};

export default MarDetailModal;
